import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { mockAppointments, mockUsers } from '../mockData';
import PatientProfileForm from '../components/PatientProfileForm';
import MedicalAgendaPanel from '../components/MedicalAgendaPanel';
import { normalizePatientProfile } from '../clinical/patientProfile';
import { APPOINTMENT_STATUSES, getAppointmentStatusLabel, normalizeAppointmentStatus } from '../clinical/appointmentStatus';
import { getTriagePriorityLabel } from '../clinical/preTriage';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const DoctorDashboard = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [appointments, setAppointments] = useState([]);
  const [consultationHistory, setConsultationHistory] = useState([]);
  const [incomingReferrals, setIncomingReferrals] = useState([]);
  const [mspForms, setMspForms] = useState([]);
  const [selectedPatientProfile, setSelectedPatientProfile] = useState(null);
  const [consentsByAppointment, setConsentsByAppointment] = useState({});
  const notifiedAppointmentsRef = useRef(new Set());
  const [stats, setStats] = useState({
    todayAppointments: 0,
    totalPatients: 0,
    completedConsultations: 0,
    averageRating: 4.8
  });
  const navigate = useNavigate();
  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  const loadDoctorAppointments = useCallback(() => {
    if (user.demoMode) {
      auditFetch(`${backendUrl}/api/demo/presentation`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Demo not found'))))
        .then((data) => {
          const doctorAppointments = (data.appointments || []).filter((apt) => apt.doctorId === user.id);
          setAppointments(doctorAppointments);
          setConsultationHistory((data.consultations || []).filter((consultation) => consultation.doctorId === user.id));
          setSelectedPatientProfile(normalizePatientProfile(doctorAppointments[0]?.patient || mockUsers.patients[0]));
          setConsentsByAppointment(Object.fromEntries(doctorAppointments.map((appointment) => [
            appointment.id,
            { status: 'accepted', accepted: true, demoMode: true },
          ])));
          setStats({
            todayAppointments: doctorAppointments.length,
            totalPatients: new Set(doctorAppointments.map((apt) => apt.patientId)).size,
            completedConsultations: (data.consultations || []).filter((consultation) => consultation.doctorId === user.id).length,
            averageRating: 4.8,
          });
        })
        .catch((error) => console.error('Error loading demo appointments:', error));
      return;
    }

    auditFetch(`${backendUrl}/api/appointments/${user.id}`, {}, auditActor)
      .then((res) => res.json())
      .then((data) => {
        const doctorAppointments = data.length > 0 ? data : mockAppointments.filter(apt => apt.doctorId === user.id);
        setAppointments(doctorAppointments);
        setSelectedPatientProfile(normalizePatientProfile(doctorAppointments[0]?.patient || mockUsers.patients[0]));

        Promise.all(doctorAppointments.map((appointment) => (
          auditFetch(`${backendUrl}/api/consents/telemedicine/${appointment.id}?patientId=${appointment.patientId}`, {}, auditActor)
            .then((res) => (res.ok ? res.json() : null))
            .then((consentData) => [appointment.id, consentData?.consent || null])
            .catch(() => [appointment.id, null])
        ))).then((entries) => {
          setConsentsByAppointment(Object.fromEntries(entries));
        });

        const today = new Date().toDateString();
        const todayAppts = doctorAppointments.filter(apt =>
          new Date(apt.datetime).toDateString() === today
        );

        setStats({
          todayAppointments: todayAppts.length,
          totalPatients: new Set(doctorAppointments.map((apt) => apt.patientId)).size,
          completedConsultations: doctorAppointments.length,
          averageRating: 4.8
        });
      })
      .catch((e) => console.error('Error loading appointments:', e));
  }, [auditActor, backendUrl, user.demoMode, user.id]);

  useEffect(() => {
    loadDoctorAppointments();
    // Load consultation history
    if (user.demoMode) {
      setMspForms([]);
      return;
    }

    auditFetch(`${backendUrl}/api/consultations/history/${user.id}`, {}, auditActor)
      .then((res) => res.json())
      .then((data) => setConsultationHistory(data))
      .catch((e) => console.error('Error loading history:', e));

    auditFetch(`${backendUrl}/api/msp/forms`, {}, auditActor)
      .then((res) => res.json())
      .then((data) => setMspForms(data))
      .catch((e) => console.error('Error loading MSP forms:', e));

    auditFetch(`${backendUrl}/api/referrals/doctor/${user.id}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : { referrals: [] }))
      .then((data) => setIncomingReferrals(Array.isArray(data.referrals) ? data.referrals : []))
      .catch((e) => console.error('Error loading incoming referrals:', e));

  }, [user.id, user.demoMode, auditActor, backendUrl, loadDoctorAppointments]);

  useEffect(() => {
    if (user.demoMode) return undefined;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const dashboardSocket = io(backendUrl || undefined);

    dashboardSocket.on('connect', () => {
      dashboardSocket.emit('user:online', {
        userId: user.id,
        role: user.role,
      });
    });

    dashboardSocket.on('consultation:participant-waiting', (data) => {
      if (data.role !== 'patient') return;

      const appointment = appointments.find(
        (apt) => apt.id === data.appointmentId && apt.doctorId === user.id
      ) || mockAppointments.find((apt) => apt.id === data.appointmentId && apt.doctorId === user.id);
      if (!appointment || notifiedAppointmentsRef.current.has(appointment.id)) return;

      notifiedAppointmentsRef.current.add(appointment.id);
      const patientName = appointment.patient?.name || 'Un paciente';
      const shouldEnter = window.confirm(`${patientName} está esperando en la consulta. ¿Deseas entrar ahora?`);

      if (shouldEnter) {
        navigate(`/consultation/${appointment.id}`, {
          state: { appointment, user },
        });
      }
    });

    return () => dashboardSocket.disconnect();
  }, [appointments, navigate, user]);

  const formatDuration = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  const handleStartConsultation = (appointment) => {
    if (!appointment.admission?.readyForDoctor) {
      const shouldContinue = window.confirm('Admision aun no marco esta cita como lista para medico. ¿Deseas iniciar de todos modos?');
      if (!shouldContinue) return;
    }

    navigate(`/consultation/${appointment.id}`, {
      state: { appointment, user }
    });
  };

  const hasAcceptedConsent = (appointmentId) => (
    consentsByAppointment[appointmentId]?.status === 'accepted'
    || consentsByAppointment[appointmentId]?.accepted === true
  );

  const handleViewPatientProfile = (appointment) => {
    const patient = appointment.patient;
    if (!patient) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    setSelectedPatientProfile(normalizePatientProfile(patient));

    auditFetch(`${backendUrl}/api/patients/${patient.id}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Patient profile not found'))))
      .then((data) => setSelectedPatientProfile(normalizePatientProfile({ ...patient, ...data.profile })))
      .catch(() => setSelectedPatientProfile(normalizePatientProfile(patient)));
  };

  const handleCreateAppointment = () => {
    const newAppointment = {
      id: 'apt-' + Date.now(),
      doctorId: user.id,
      doctor: user,
      patientId: mockUsers.patients[0].id,
      patient: mockUsers.patients[0],
      datetime: new Date(Date.now() + 7200000).toISOString(),
      reason: 'Consulta de seguimiento',
      status: APPOINTMENT_STATUSES.pending,
    };
    mockAppointments.push(newAppointment);
    setAppointments([...appointments, newAppointment]);
  };

  const openReferralPdf = (referral) => {
    fetch(`${backendUrl}/api/referrals/${referral.id}/pdf`, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo abrir HCU 053');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => console.error('Error opening referral PDF:', error));
  };

  return (
    <div className="dashboard doctor-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Dashboard Médico</h1>
          <p className="subtitle">Bienvenido, {user.name} · {user.specialty}</p>
        </div>

        {incomingReferrals.length > 0 && (
          <section className="incoming-referrals-panel">
            <div className="section-header">
              <div>
                <h2>Interconsultas recibidas</h2>
                <p>HCU 053 asignadas para revision como medico receptor.</p>
              </div>
              <span>{incomingReferrals.length} pendiente(s)</span>
            </div>
            <div className="incoming-referrals-list">
              {incomingReferrals.slice(0, 4).map((referral) => (
                <article key={referral.id} className={`incoming-referral-card referral-status-${referral.status}`}>
                  <div>
                    <strong>{referral.patient?.name || 'Paciente'} · {referral.targetSpecialty}</strong>
                    <span>{referral.reason || 'Motivo pendiente'}</span>
                    <small>{referral.requestedAction || referral.clinicalSummary || 'Resumen clinico disponible en HCU 053'}</small>
                  </div>
                  <div className="incoming-referral-actions">
                    <span>{referral.status || 'solicitada'}</span>
                    <button type="button" onClick={() => openReferralPdf(referral)}>HCU 053</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Stats Cards - Key for Executive Presentation */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <h3>{stats.todayAppointments}</h3>
              <p>Citas Hoy</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>{stats.totalPatients}</h3>
              <p>Pacientes Activos</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{stats.completedConsultations}</h3>
              <p>Consultas Completadas</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <h3>{stats.averageRating}</h3>
              <p>Calificación Promedio</p>
            </div>
          </div>
        </div>

        <div className="dashboard-content">
          <section className="appointments-section">
            <MedicalAgendaPanel user={user} mode="doctor" doctorFilter={user.id} />

            <div className="section-header">
              <h2>📅 Próximas Citas</h2>
              <button className="btn btn-primary" onClick={handleCreateAppointment}>
                + Nueva Cita
              </button>
            </div>

            {appointments.length === 0 ? (
              <div className="empty-state">
                <p>No hay citas programadas</p>
                <button className="btn btn-primary" onClick={handleCreateAppointment}>
                  Crear una cita de presentacion
                </button>
              </div>
            ) : (
              <div className="appointments-grid">
                {appointments.map((apt) => (
                  <div key={apt.id} className="appointment-card">
                    <div className="apt-header">
                      <h3 className="patient-name">{apt.patient?.name || 'Paciente'}</h3>
                      <span className={`status-badge status-${normalizeAppointmentStatus(apt.status)}`}>
                        {getAppointmentStatusLabel(apt.status)}
                      </span>
                    </div>
                    <div className="apt-details">
                      <p className="apt-reason">
                        <strong>Motivo:</strong> {apt.reason}
                      </p>
                      <p className="apt-time">
                        <strong>Hora:</strong> {new Date(apt.datetime).toLocaleString('es-ES')}
                      </p>
                      <p className="apt-insurance">
                        <strong>Seguro:</strong> {apt.patient?.insurance || 'No especificado'}
                      </p>
                      <p className="apt-insurance">
                        <strong>Consentimiento:</strong>{' '}
                        <span className={hasAcceptedConsent(apt.id) ? 'status-online' : 'status-offline'}>
                          {hasAcceptedConsent(apt.id) ? 'Aceptado' : 'Pendiente'}
                        </span>
                      </p>
                      <p className="apt-insurance">
                        <strong>Admision:</strong>{' '}
                        <span className={apt.admission?.readyForDoctor ? 'status-online' : 'status-offline'}>
                          {apt.admission?.readyForDoctor ? 'Lista para medico' : 'Pendiente de revision'}
                        </span>
                      </p>
                      <p className="apt-insurance">
                        <strong>Pretriaje:</strong>{' '}
                        <span className={`triage-priority triage-priority-${apt.preTriage?.priority || 'baja'}`}>
                          {apt.preTriage ? getTriagePriorityLabel(apt.preTriage.priority) : 'Pendiente'}
                        </span>
                      </p>
                      {apt.preTriage?.mainSymptoms && (
                        <p className="apt-insurance">
                          <strong>Sintomas:</strong> {apt.preTriage.mainSymptoms}
                        </p>
                      )}
                      {apt.admission?.updatedAt && (
                        <p className="apt-insurance">
                          <strong>Actualizada:</strong> {new Date(apt.admission.updatedAt).toLocaleString('es-EC')}
                        </p>
                      )}
                    </div>
                    <div className="apt-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleViewPatientProfile(apt)}
                      >
                        Ver ficha
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={() => handleStartConsultation(apt)}
                      >
                        Iniciar Consulta
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <aside className="dashboard-sidebar">
            <div className="info-card">
              <h3>👨‍⚕️ Mi Perfil</h3>
              <p><strong>Nombre:</strong> {user.name}</p>
              <p><strong>Especialidad:</strong> {user.specialty || 'Medicina General'}</p>
              <p><strong>Usuario:</strong> {user.username}</p>
              <p><strong>Cédula/Registro:</strong> {user.documentId || 'No registrado'}</p>
              <p><strong>Email:</strong> {user.email || 'maria@clinic.com'}</p>
              <p><strong>Teléfono:</strong> {user.phone || 'No registrado'}</p>
              <p><strong>Estado:</strong> <span className="status-online">🟢 Disponible</span></p>
            </div>

            <div className="info-card">
              <h3>� Historial de Consultas</h3>
              {consultationHistory.length === 0 ? (
                <p style={{ fontSize: '0.9rem', color: '#999' }}>No hay consultas registradas</p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {consultationHistory.map((consultation) => (
                    <div
                      key={consultation.id}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '0.9rem',
                      }}
                    >
                      <p style={{ margin: '0.25rem 0', fontWeight: 600 }}>
                        {consultation.patientName}
                      </p>
                      <p style={{ margin: '0.25rem 0', color: '#666' }}>
                        ⏱️ Duración: {formatDuration(consultation.duration)}
                      </p>
                      <p style={{ margin: '0.25rem 0', color: '#999', fontSize: '0.85rem' }}>
                        {new Date(consultation.endedAt).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="info-card">
              <h3>Pacientes asignados</h3>
              <ul>
                {appointments.map((apt) => (
                  <li key={apt.id}>
                    {apt.patient?.name} · {apt.patient?.plan || apt.patient?.insurance} · {apt.patient?.serviceActive ? 'Activo' : 'Inactivo'}
                  </li>
                ))}
              </ul>
            </div>

            {selectedPatientProfile && (
              <div className="info-card patient-profile-card">
                <PatientProfileForm
                  profile={selectedPatientProfile}
                  onChange={setSelectedPatientProfile}
                  readOnly
                  compact
                />
              </div>
            )}

            <div className="info-card">
              <h3>Gestión clínica</h3>
              <p>Selecciona una cita para iniciar video, completar HCU 002, registrar diagnósticos y guardar la historia clínica.</p>
            </div>

            <div className="info-card">
              <h3>Formularios MSP</h3>
              {mspForms.length === 0 ? (
                <p style={{ fontSize: '0.9rem', color: '#999' }}>Cargando catálogo clínico...</p>
              ) : (
                <ul>
                  {mspForms.map((form) => (
                    <li key={form.id}>
                      <strong>{form.code}</strong> · {form.name} · {form.status === 'active' ? 'Activo' : 'Planificado'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
